import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

const ASPECTOS_MUJER = [
  { id: "cabello", nombre: "Cabello Recogido" },
  { id: "maquillaje", nombre: "Maquillaje Sobrio" },
  { id: "unas", nombre: "Uñas Cortas y Sin Esmalte" },
  { id: "joyas", nombre: "Sin Joyas" },
  { id: "dotacion", nombre: "Dotación y EPP" },
  { id: "locion", nombre: "Sin Loción Fuerte" },
  { id: "heridas", nombre: "Sin Heridas / Usa Guantes" }
];

const ASPECTOS_HOMBRE = [
  { id: "cabello", nombre: "Corte de Cabello Bajo" },
  { id: "joyas", nombre: "Sin Joyas" },
  { id: "barba", nombre: "Sin Barba / Con Tapabocas" },
  { id: "dotacion", nombre: "Dotación y EPP" },
  { id: "locion", nombre: "Sin Loción Fuerte" },
  { id: "heridas", nombre: "Sin Heridas / Usa Guantes" },
  { id: "unas", nombre: "Uñas Cortas y Limpias" }
];

const ASPECTOS_SALUD = [
  { id: "salud_cortaduras", nombre: "Cortaduras" },
  { id: "salud_tos", nombre: "Tos" },
  { id: "salud_digestivos", nombre: "Síntomas digestivos" }, 
  { id: "salud_respiratorios", nombre: "Síntomas respiratorios" }
];

export default function FormatoPracticasHigienicas({ usuario, onBack }) {
  const [datos, setDatos] = useState({
    fechaEvento: new Date().toISOString().split('T')[0],
    turno: "8:00 AM",
    pdv: "",
    documento: "",
    nombre: "",
    cargo: "",
    genero: "MUJER",
    accionCorrectiva: ""
  });

  const [listaSedes, setListaSedes] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });
  const [empleadosDB, setEmpleadosDB] = useState([]);
  const [cargandoEmpleados, setCargandoEmpleados] = useState(false);
  const [buscarTodasSedes, setBuscarTodasSedes] = useState(false);

  const permisosABAC = useMemo(() => {
    try {
      const parsed = typeof usuario?.permisos_avanzados === 'string' ? JSON.parse(usuario.permisos_avanzados) : (usuario?.permisos_avanzados || {});
      return parsed.micros || parsed; 
    } catch (e) { return {}; }
  }, [usuario]);

  useEffect(() => {
    const cargarDatosMaestros = async () => {
      setCargandoEmpleados(true);
      try {
        const token = localStorage.getItem("token");
        const API_URL = process.env.REACT_APP_API_URL;
        
        const resSedes = await fetch(`${API_URL}/api/sedes`);
        const dataSedes = await resSedes.json();
        
        if (dataSedes.success && Array.isArray(dataSedes.sedes)) {
          const cargoUpper = String(usuario?.cargo || "").toUpperCase();
          const esAdminGlobal = cargoUpper === "SUPER_ADMIN";
          
          const configPermiso = permisosABAC['Otros Formatos SST'] || {};
          const alcance = configPermiso.alcance || "PROPIA";
          const sedesEspecificas = configPermiso.sedes_ids || [];
          
          let filtradas = dataSedes.sedes;
          if (!esAdminGlobal && alcance !== "GLOBAL") {
            if (alcance === "ESPECIFICA" && sedesEspecificas.length > 0) {
              filtradas = dataSedes.sedes.filter(s => sedesEspecificas.includes(String(s.id)) || sedesEspecificas.includes(String(s.codigo_bodega)));
            } else {
              filtradas = dataSedes.sedes.filter(s => String(s.codigo_bodega) === String(usuario?.codigo_bodega || usuario?.sede_id));
            }
          }
          setListaSedes(filtradas);
          if (filtradas.length === 1) {
            setDatos(prev => ({ ...prev, pdv: filtradas[0].nombre.toUpperCase() }));
          }
        }

        const resEmp = await fetch(`${API_URL}/api/empleados`, {
          headers: { ...(token && { Authorization: `Bearer ${token}` }) }
        });
        const dataEmp = await resEmp.json();
        const listaEmpleados = Array.isArray(dataEmp) ? dataEmp : (dataEmp.data || []);
        setEmpleadosDB(listaEmpleados);
      } catch (error) {
        console.error("Error cargando BD:", error);
      } finally {
        setCargandoEmpleados(false);
      }
    };
    cargarDatosMaestros();
  }, [usuario, permisosABAC]);

  const listaParaMostrar = empleadosDB.filter(emp => {
    const estaActivo = !emp.empl_fecret || String(emp.empl_fecret).toUpperCase() === 'NULL' || emp.empl_fecret === '0000-00-00';
    if (!estaActivo) return false;
    if (buscarTodasSedes) return true; 
    if (!emp.area_nombre || !datos.pdv) return false;
    return emp.area_nombre.toUpperCase() === datos.pdv.toUpperCase();
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'pdv') {
      setDatos(prev => ({ ...prev, pdv: value, documento: "", nombre: "", cargo: "" }));
      setRespuestas({});
    } else if (name === 'nombre') {
      const empSeleccionado = listaParaMostrar.find(emp => emp.terc_nom === value);
      if (empSeleccionado) {
        setDatos(prev => ({ ...prev, nombre: empSeleccionado.terc_nom, documento: empSeleccionado.terc_nit, cargo: empSeleccionado.empl_cargo }));
        setRespuestas({}); 
      } else {
        setDatos(prev => ({ ...prev, nombre: value })); 
      }
    } else {
      setDatos(prev => ({ ...prev, [name]: value }));
      if (name === 'genero') setRespuestas({});
    }
  };

  const setRespuesta = (id, valor) => {
    setRespuestas({ ...respuestas, [id]: valor });
  };

  const marcarTodoCumple = () => {
    const nuevasRespuestas = {};
    const aspectos = datos.genero === 'MUJER' ? ASPECTOS_MUJER : ASPECTOS_HOMBRE;
    aspectos.forEach(item => { nuevasRespuestas[item.id] = 'SI'; });
    nuevasRespuestas["salud_cortaduras"] = "NO";
    nuevasRespuestas["salud_tos"] = "NO";
    nuevasRespuestas["salud_digestivos"] = "NO"; 
    nuevasRespuestas["salud_respiratorios"] = "NO";
    setRespuestas(nuevasRespuestas);
  };

  const handleSubmit = async () => {
    if (!datos.pdv || !datos.documento || !datos.nombre || !datos.turno || !datos.cargo) {
      return setCustomAlert({ show: true, message: "Sede, Turno, Documento, Nombre y Cargo son obligatorios.", type: "warning" });
    }
    
    const aspectosRequeridos = datos.genero === 'MUJER' ? ASPECTOS_MUJER : ASPECTOS_HOMBRE;
    if (Object.keys(respuestas).length < (aspectosRequeridos.length + ASPECTOS_SALUD.length)) {
      return setCustomAlert({ show: true, message: "Debes evaluar todos los aspectos higiénicos y de salud.", type: "warning" });
    }

    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_API_URL;
      
      const evaluatorName = `${usuario?.nombres || ''} ${usuario?.apellidos || ''}`.trim() || "Auditor SST";

      const payload = {
        tipo_formulario: "PRACTICAS_HIGIENICAS_SST",
        datosGenerales: { 
          pdv: datos.pdv, 
          fecha: datos.fechaEvento, 
          evaluador: evaluatorName
        },
        respuestas: {
          turno: datos.turno,
          documento: datos.documento,
          empleado_evaluado: datos.nombre, 
          cargo: datos.cargo,
          genero: datos.genero,
          accion_correctiva: datos.accionCorrectiva,
          ...respuestas
        },
        puntaje_global: 100
      };

      const res = await fetch(`${API_URL}/api/sst/guardar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify(payload)
      });

      const dataRes = await res.json();
      if (dataRes.success) {
        setCustomAlert({ show: true, message: "Evaluación guardada exitosamente.", type: "success" });
        setTimeout(() => {
          setDatos(prev => ({ ...prev, documento: "", nombre: "", cargo: "", accionCorrectiva: "" }));
          setRespuestas({});
          setCustomAlert({ ...customAlert, show: false });
        }, 1200);
      } else {
        setCustomAlert({ show: true, message: dataRes.message || "Error al guardar.", type: "error" });
      }
    } catch (error) {
      setCustomAlert({ show: true, message: "Error de conexión con el servidor.", type: "error" });
    }
  };

  const aspectosActuales = datos.genero === 'MUJER' ? ASPECTOS_MUJER : ASPECTOS_HOMBRE;
  const inputClass = "w-full h-10 px-3 bg-white/80 border-transparent focus:border-fuchsia-400 rounded-xl text-xs font-bold text-slate-800 shadow-sm transition-all outline-none cursor-pointer";

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-4xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      <div className="bg-gradient-to-r from-fuchsia-600/90 to-purple-800/90 border-b border-white/20 px-6 py-4 z-40 flex items-center shadow-md shrink-0">
        <button onClick={onBack} className="mr-4 w-10 h-10 flex-none bg-white/20 hover:bg-white/40 text-white flex items-center justify-center rounded-full transition-all border border-white/40 shadow-sm cursor-pointer">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        <div>
          <h1 className="text-lg sm:text-xl font-black text-white leading-tight">Prácticas Higiénicas</h1>
          <span className="bg-white/20 text-white border border-white/30 text-[10px] font-bold px-2 py-0.5 rounded-md mt-1 inline-block">CÓDIGO: FT-CD-01 | Versión: 3</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 custom-scrollbar">
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Fecha *</label>
              <input type="date" name="fechaEvento" value={datos.fechaEvento} onChange={handleChange} className={inputClass} />
            </div>
            
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Sede *</label>
              <div className="relative w-full h-10">
                <select name="pdv" value={datos.pdv} onChange={handleChange} className={`${inputClass} appearance-none pr-8 absolute inset-0`}>
                  <option value="" disabled>-- Seleccione sede --</option>
                  {listaSedes.map(sede => (
                    <option key={sede.id} value={sede.nombre.toUpperCase()}>{sede.nombre}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 text-[10px] font-black">▼</div>
              </div>
            </div>

            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Turno *</label>
              <div className="relative w-full h-10">
                <select name="turno" value={datos.turno} onChange={handleChange} className={`${inputClass} appearance-none pr-8 absolute inset-0`}>
                  <option value="8:00 AM">8:00 AM (Mañana)</option><option value="2:00 PM">2:00 PM (Tarde)</option><option value="10:00 PM">10:00 PM (Noche)</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 text-[10px] font-black">▼</div>
              </div>
            </div>
            
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Género *</label>
              <div className="relative w-full h-10">
                <select name="genero" value={datos.genero} onChange={handleChange} className={`${inputClass} appearance-none pr-8 absolute inset-0`}>
                  <option value="MUJER">MUJER</option><option value="HOMBRE">HOMBRE</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 text-[10px] font-black">▼</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-slate-200/60 pt-4">
            <div className="relative flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Nombre del Empleado *</label>
              <input 
                type="text" name="nombre" list="listaEmpleados" value={datos.nombre} onChange={handleChange} 
                placeholder={cargandoEmpleados ? "Cargando BD..." : datos.pdv ? "Escribe o elige nombre..." : "Elige sede primero"} 
                disabled={!datos.pdv || cargandoEmpleados}
                className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`} autoComplete="off"
              />
              <datalist id="listaEmpleados">
                {listaParaMostrar.map((emp, i) => (<option key={i} value={emp.terc_nom}>{buscarTodasSedes ? `${emp.empl_cargo} (${emp.area_nombre})` : emp.empl_cargo}</option>))}
              </datalist>
              <div className="mt-1 flex items-center">
                <input type="checkbox" id="buscarGlobal" checked={buscarTodasSedes} onChange={(e) => setBuscarTodasSedes(e.target.checked)} className="mr-1.5 cursor-pointer accent-fuchsia-600" disabled={!datos.pdv} />
                <label htmlFor="buscarGlobal" className="text-[9px] text-fuchsia-700 font-black tracking-wide cursor-pointer uppercase">Buscar en todas las sedes</label>
              </div>
            </div>
            
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Cédula / Documento *</label>
              <input type="text" name="documento" placeholder="Automático" value={datos.documento} onChange={handleChange} className={inputClass} />
            </div>
            
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Cargo *</label>
              <input type="text" name="cargo" placeholder="Automático" value={datos.cargo} onChange={handleChange} className={inputClass} />
            </div>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-5 border-b border-fuchsia-200/50 pb-4 gap-3">
            <h3 className="text-xs font-black text-fuchsia-800 uppercase tracking-widest">Prácticas Higiénicas: {datos.genero}</h3>
            <button type="button" onClick={marcarTodoCumple} className="bg-emerald-500 hover:bg-emerald-400 text-white text-[10px] font-black px-5 py-2.5 rounded-xl shadow-sm transition-transform active:scale-95 uppercase tracking-wide cursor-pointer w-full sm:w-auto">✓ Todo Ideal (SI/NO)</button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {aspectosActuales.map(item => (
              <div key={item.id} className="flex justify-between items-center bg-white/50 p-3 rounded-xl border border-slate-100 shadow-sm">
                <span className="text-xs font-bold text-slate-700">{item.nombre}</span>
                <div className="flex gap-1.5 bg-slate-200/60 p-1 rounded-lg">
                  <button type="button" onClick={() => setRespuesta(item.id, 'SI')} className={`px-4 py-1.5 rounded-md text-[10px] font-black transition-all cursor-pointer ${respuestas[item.id] === 'SI' ? 'bg-emerald-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white hover:text-slate-800'}`}>SI</button>
                  <button type="button" onClick={() => setRespuesta(item.id, 'NO')} className={`px-4 py-1.5 rounded-md text-[10px] font-black transition-all cursor-pointer ${respuestas[item.id] === 'NO' ? 'bg-red-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white hover:text-slate-800'}`}>NO</button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-fuchsia-200/50">
            <h3 className="text-xs font-black text-fuchsia-800 uppercase tracking-widest mb-4">Estado de Salud</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ASPECTOS_SALUD.map(item => (
                <div key={item.id} className="flex justify-between items-center bg-white/50 p-3 rounded-xl border border-slate-100 shadow-sm">
                  <span className="text-xs font-bold text-slate-700">{item.nombre}</span>
                  <div className="flex gap-1.5 bg-slate-200/60 p-1 rounded-lg">
                    <button type="button" onClick={() => setRespuesta(item.id, 'SI')} className={`px-4 py-1.5 rounded-md text-[10px] font-black transition-all cursor-pointer ${respuestas[item.id] === 'SI' ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white hover:text-slate-800'}`}>SI</button>
                    <button type="button" onClick={() => setRespuesta(item.id, 'NO')} className={`px-4 py-1.5 rounded-md text-[10px] font-black transition-all cursor-pointer ${respuestas[item.id] === 'NO' ? 'bg-red-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white hover:text-slate-800'}`}>NO</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 pt-5 border-t border-slate-200/50">
            <label className="block text-[10px] font-black text-slate-700 mb-2 uppercase tracking-widest">Acción Correctiva (Si algún ítem no cumple)</label>
            <input type="text" name="accionCorrectiva" value={datos.accionCorrectiva} onChange={handleChange} placeholder="Ej: Se solicitó retirar maquillaje excesivo o reportó tos..." className="w-full h-10 bg-white/80 border border-slate-200 focus:border-fuchsia-400 rounded-xl px-4 text-xs font-medium text-slate-800 shadow-sm outline-none" />
          </div>

          <button onClick={handleSubmit} className="w-full mt-6 bg-gradient-to-r from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 text-white font-black py-4 rounded-xl shadow-lg active:scale-95 transition-all text-[11px] tracking-widest uppercase cursor-pointer border border-fuchsia-400">
            Guardar Evaluación
          </button>
        </div>
      </div>

      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : 'Atención'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className={`w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer ${customAlert.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-500 hover:bg-amber-400'}`}>Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}